import { soundManager } from "../../SoundManager";

/**
 * A looping sound shared by several spinners, counted rather than toggled.
 *
 * ── Why counting, and not a boolean ──
 *
 * Two different features spin a strip and want a loop under it, and in both of
 * them SEVERAL strips run at once: the Golden Mystery opens a whole row of
 * cells together, and each one starts and stops on its own schedule.
 *
 * `play()` on a looping Howl does not restart it — it spawns a SECOND sound id,
 * so ten cells would stack ten copies of the same loop. And a plain "stop when
 * I finish" would cut the loop the moment the FIRST cell landed, while the rest
 * were still turning.
 *
 * So it is counted: the first spinner to start it plays it, and the last one to
 * stop ends it. That is exactly the rule the loop is meant to express — it runs
 * from the moment the coins start moving until the last one has landed.
 *
 * Keyed by sound NAME, so two features naming two different loops never share a
 * count.
 *
 * Callers must hold their own "I am counted" flag and make their release
 * idempotent: a cell destroyed mid-spin has to give its reference back, or the
 * loop plays for ever. See `_spinSoundOn` in both controllers.
 */
const _refs = new Map();

/** Take a reference. Plays the sound if this is the first one. */
export function acquireLoopSound(name) {
  if (!name) return;
  const next = (_refs.get(name) || 0) + 1;
  _refs.set(name, next);
  if (next === 1) soundManager.play(name);
}

/** Give a reference back. Stops the sound when the last one goes. */
export function releaseLoopSound(name) {
  if (!name) return;
  const current = _refs.get(name) || 0;
  if (current <= 0) return;
  const next = current - 1;
  if (next === 0) {
    _refs.delete(name);
    soundManager.stop(name);
  } else {
    _refs.set(name, next);
  }
}
